import React, { useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, Button, message } from 'antd';
import { useCreate } from '@refinedev/core';
import { TRANSACTION_TYPE_OPTIONS } from '../constants';

const { TextArea } = Input;

// Type definitions
interface UnitDefinition {
  type: 'weight' | 'volume' | 'length' | 'count';
  toBase: (v: number) => number;
  base: string;
}

interface UnitMap {
  [key: string]: UnitDefinition;
}

interface ParsedQuantity {
  original: string;
  value: number;
  unit: string;
  type: string;
  baseValue: number;
  baseUnit: string;
}

// Step 1: unit synonyms
const unitAliases: { [key: string]: string[] } = {
  // solids
  kg: ['kg', 'kgs', 'kilogram', 'kilograms'],
  g: ['g', 'gram', 'grams'],
  mg: ['mg', 'milligram', 'milligrams'],
  ton: ['ton', 'tons', 't'],
  lb: ['lb', 'lbs', 'pound', 'pounds'],
  oz: ['oz', 'ounce', 'ounces'],
  pcs: ['pc', 'pcs', 'piece', 'pieces', 'unit', 'nos'],

  // liquids
  l: ['l', 'litre', 'liter', 'liters', 'litres'],
  ml: ['ml', 'millilitre', 'milliliter', 'millilitres', 'milliliters'],
  cl: ['cl', 'centilitre', 'centiliter'],
  gal: ['gal', 'gallon', 'gallons'],
  qt: ['qt', 'quart', 'quarts'],
  pt: ['pt', 'pint', 'pints'],
  floz: ['fl oz', 'fluidounce', 'fluidounces'],

  // gases
  m3: ['m3', 'm^3', 'cubicmeter', 'cubicmeters'],
  cm3: ['cm3', 'cubiccentimeter', 'cubiccentimetre'],
  ft3: ['ft3', 'cubicfoot', 'cubicfeet'],

  // packaged
  box: ['box', 'boxes'],
  packet: ['packet', 'packets', 'pack', 'packs', 'pkt'],
  dozen: ['dozen', 'dozens'],
  set: ['set', 'sets'],
  pair: ['pair', 'pairs'],
};

// Step 2: canonical unit definitions
const unitMap: UnitMap = {
  // Weight
  mg: { type: 'weight', toBase: (v: number) => v / 1000, base: 'g' },
  g: { type: 'weight', toBase: (v: number) => v, base: 'g' },
  kg: { type: 'weight', toBase: (v: number) => v * 1000, base: 'g' },
  ton: { type: 'weight', toBase: (v: number) => v * 1_000_000, base: 'g' },
  lb: { type: 'weight', toBase: (v: number) => v * 453.592, base: 'g' },
  oz: { type: 'weight', toBase: (v: number) => v * 28.3495, base: 'g' },

  // Volume
  ml: { type: 'volume', toBase: (v: number) => v, base: 'ml' },
  l: { type: 'volume', toBase: (v: number) => v * 1000, base: 'ml' },
  cl: { type: 'volume', toBase: (v: number) => v * 10, base: 'ml' },
  gal: { type: 'volume', toBase: (v: number) => v * 3785.41, base: 'ml' },
  qt: { type: 'volume', toBase: (v: number) => v * 946.353, base: 'ml' },
  pt: { type: 'volume', toBase: (v: number) => v * 473.176, base: 'ml' },
  floz: { type: 'volume', toBase: (v: number) => v * 29.5735, base: 'ml' },

  // Length
  mm: { type: 'length', toBase: (v: number) => v / 1000, base: 'm' },
  cm: { type: 'length', toBase: (v: number) => v / 100, base: 'm' },
  m: { type: 'length', toBase: (v: number) => v, base: 'm' },
  km: { type: 'length', toBase: (v: number) => v * 1000, base: 'm' },
  in: { type: 'length', toBase: (v: number) => v * 0.0254, base: 'm' },
  ft: { type: 'length', toBase: (v: number) => v * 0.3048, base: 'm' },
  yd: { type: 'length', toBase: (v: number) => v * 0.9144, base: 'm' },

  // Count
  pcs: { type: 'count', toBase: (v: number) => v, base: 'pcs' },
  box: { type: 'count', toBase: (v: number) => v, base: 'box' },
  packet: { type: 'count', toBase: (v: number) => v, base: 'packet' },
  dozen: { type: 'count', toBase: (v: number) => v * 12, base: 'pcs' },
  set: { type: 'count', toBase: (v: number) => v, base: 'set' },
  pair: { type: 'count', toBase: (v: number) => v * 2, base: 'pcs' },
};

// Step 3: normalize unit alias → canonical
function normalizeUnit(inputUnit: string): string | null {
  if (!inputUnit) return null;

  // Remove all spaces first, then convert to lowercase
  const u = inputUnit.replace(/\s+/g, '').toLowerCase();

  // First, try to extract unit from pack size formats
  // Case: "10 x 10" or "1*12" or "1 * 12" or "1x12"
  const matchMultiply = u.match(/(\d+(?:\.\d+)?)\s*[x*]\s*(\d+(?:\.\d+)?)/);
  if (matchMultiply) {
    // For pack sizes, we don't normalize - return null to use extractNumberFromUnit
    return null;
  }

  // Case: Combo kit like (2+1+3)
  const matchCombo = u.match(/^\(?\s*\d+(?:\s*\+\s*\d+)+\s*\)?$/);
  if (matchCombo) {
    // For combo kits, we don't normalize - return null to use extractNumberFromUnit
    return null;
  }

  // Case: Units like cap, tab, sachet
  const matchUnit = u.match(
    /^(\d+(?:\.\d+)?)\s*(cap|tab|sachet|vial|ampoule)$/
  );
  if (matchUnit) {
    // For specific units, we don't normalize - return null to use extractNumberFromUnit
    return null;
  }

  // Case: Weight/Volume like 100 gm, 30 ml, 1.5l, 2kilograms, 10ounces
  const matchPack = u.match(
    /^(\d+(?:\.\d+)?)\s*(gm|ml|kg|l|gram|grams|kilogram|kilograms|litre|liter|liters|litres|ounce|ounces|oz|pound|pounds|lb|ton|tons|gallon|gallons|gal|quart|quarts|qt|pint|pints|pt|fluidounce|fluidounces|floz|milligram|milligrams|mg|centilitre|centiliter|cl|dozen|dozens|box|boxes|packet|packets|pack|packs|pkt|set|sets|pair|pairs|piece|pieces|pc|pcs|unit|nos)$/
  );
  if (matchPack) {
    // Extract just the unit part and normalize it
    const unitPart = matchPack[2];
    for (const [standard, variations] of Object.entries(unitAliases)) {
      if (variations.includes(unitPart)) return standard;
    }
    return null;
  }

  // Case: Just a number
  const matchNumber = u.match(/^(\d+(?:\.\d+)?)$/);
  if (matchNumber) {
    // For just numbers, we don't normalize - return null to use extractNumberFromUnit
    return null;
  }

  // If none of the above patterns match, try direct unit matching
  for (const [standard, variations] of Object.entries(unitAliases)) {
    if (variations.includes(u)) return standard;
  }

  return null;
}

// Step 4: parseQuantity
function parseQuantity(input: string): ParsedQuantity {
  const match = input
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .match(/^([\d.]+)([a-zA-Z]+)$/);
  if (!match)
    throw new Error("Invalid format. Example: '5kg', '300ml', '2 dozen'");

  const value = parseFloat(match[1]);
  const rawUnit = match[2];
  const unit = normalizeUnit(rawUnit);

  if (!unit || !unitMap[unit]) throw new Error(`Unknown unit: ${rawUnit}`);

  const { type, toBase, base } = unitMap[unit];

  return {
    original: input,
    value,
    unit,
    type,
    baseValue: toBase(value),
    baseUnit: base,
  };
}

// Function to extract number from unit string (e.g., "100GM" -> 100, "1*12" -> 12, "1.5L" -> 1.5)
function extractNumberFromUnit(unitString: string): number {
  if (!unitString) return 1;

  // Remove all spaces first, then convert to lowercase
  const unitStr = unitString.replace(/\s+/g, '').toLowerCase().trim();

  // Case: "10 x 10" or "1*12" or "1 * 12" or "1x12"
  const matchMultiply = unitStr.match(
    /(\d+(?:\.\d+)?)\s*[x*]\s*(\d+(?:\.\d+)?)/
  );
  if (matchMultiply) {
    return parseFloat(matchMultiply[1]) * parseFloat(matchMultiply[2]);
  }

  // Case: Combo kit like (2+1+3)
  const matchCombo = unitStr.match(/^\(?\s*\d+(?:\s*\+\s*\d+)+\s*\)?$/);
  if (matchCombo) {
    // strip any parens and spaces, then sum the numbers
    const total = matchCombo[0]
      .replace(/[()\ ]/g, '') // remove ( ), spaces
      .split('+')
      .map(Number)
      .reduce((a, b) => a + b, 0);
    return total;
  }

  // Case: Units like cap, tab, sachet
  const matchUnit = unitStr.match(
    /^(\d+(?:\.\d+)?)\s*(cap|tab|sachet|vial|ampoule)$/
  );
  if (matchUnit) {
    return parseFloat(matchUnit[1]);
  }

  // Case: Weight/Volume like 100 gm, 30 ml, 1.5l, 2kilograms, 10ounces
  const matchPack = unitStr.match(
    /^(\d+(?:\.\d+)?)\s*(gm|ml|kg|l|gram|grams|kilogram|kilograms|litre|liter|liters|litres|ounce|ounces|oz|pound|pounds|lb|ton|tons|gallon|gallons|gal|quart|quarts|qt|pint|pints|pt|fluidounce|fluidounces|floz|milligram|milligrams|mg|centilitre|centiliter|cl|dozen|dozens|box|boxes|packet|packets|pack|packs|pkt|set|sets|pair|pairs|piece|pieces|pc|pcs|unit|nos)$/
  );
  if (matchPack) {
    return parseFloat(matchPack[1]);
  }

  // Case: Just a number (including decimals)
  const matchNumber = unitStr.match(/^(\d+(?:\.\d+)?)$/);
  if (matchNumber) {
    return parseFloat(matchNumber[1]);
  }

  // Default fallback
  return 1;
}

interface StockMovementFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit?: (values: any) => void;
  onSuccess?: () => void;
  loading?: boolean;
  defaultTransactionType?: 'PURCHASE' | 'USAGE';
  item: any;
}

export const StockMovementForm: React.FC<StockMovementFormProps> = ({
  visible,
  onCancel,
  onSubmit,
  onSuccess,
  loading = false,
  defaultTransactionType = 'PURCHASE',
  item,
}) => {
  const [form] = Form.useForm();
  const { mutate: createStockMovement, isLoading: isCreating } = useCreate();

  // Handle quantity change to auto-calculate additional quantity
  const handleQuantityChange = (value: number | null) => {
    if (value === null || !item?.unit) {
      form.setFieldsValue({ add_quantity: 0 });
      return;
    }

    try {
      // Get current transaction type
      const transactionType = form.getFieldValue('transaction_type');

      // For USAGE transactions, use the quantity as-is without multiplication
      if (transactionType === 'USAGE') {
        form.setFieldsValue({
          add_quantity: Math.round(value * 100) / 100,
        });
        return;
      }

      // For PURCHASE transactions, use the unit conversion logic
      // Convert item unit to lowercase first
      const itemUnitLower = item.unit.toLowerCase();

      // Check if the unit exists in our unit map
      const normalizedUnit = normalizeUnit(itemUnitLower);

      if (normalizedUnit && unitMap[normalizedUnit]) {
        // Use the unit conversion logic
        const unitInfo = unitMap[normalizedUnit];

        // For units like "1.5l" or "2kilograms", we need to extract the number part
        // and multiply it with the entered quantity before conversion
        const unitNumber = extractNumberFromUnit(item.unit);
        const totalQuantity = value * unitNumber;
        const calculatedQuantity = unitInfo.toBase(totalQuantity);

        form.setFieldsValue({
          add_quantity: Math.round(calculatedQuantity * 100) / 100,
        });
      } else {
        // Fallback: Extract number from unit string (e.g., "100GM" -> 100)
        const unitMultiplier = extractNumberFromUnit(item.unit);
        const calculatedQuantity = value * unitMultiplier;

        form.setFieldsValue({
          add_quantity: Math.round(calculatedQuantity * 100) / 100,
        });
      }
    } catch (error) {
      form.setFieldsValue({ add_quantity: value });
    }
  };

  // Handle transaction type change to recalculate additional quantity
  const handleTransactionTypeChange = (value: string) => {
    const currentQuantity = form.getFieldValue('quantity');
    if (currentQuantity) {
      handleQuantityChange(currentQuantity);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (onSubmit) {
        await onSubmit(values);
      } else {
        // Use data provider to create stock movement
        createStockMovement(
          {
            resource: 'inside-inventory-update-stock',
            values: {
              ...values,
              item_id: item.id,
            },
          },
          {
            onSuccess: () => {
              message.success('Stock movement added successfully');
              form.resetFields();
              onCancel();
              // Call onSuccess callback to trigger table refresh
              onSuccess?.();
            },
            onError: () => {
              message.error('Failed to add stock movement');
            },
          }
        );
      }
    } catch (error) {
      // Form validation error
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={`${defaultTransactionType === 'PURCHASE' ? 'Add' : 'Usage'} Stock - ${item?.name || 'Item'} (${item?.unit || 'N/A'})`}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading || isCreating}
          onClick={handleSubmit}
        >
          Add Movement
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          transaction_type: defaultTransactionType,
          quantity: 0,
          add_quantity: 0,
        }}
      >
        <Form.Item
          name="transaction_type"
          label="Transaction Type"
          rules={[
            { required: true, message: 'Please select transaction type' },
          ]}
        >
          <Select
            placeholder="Select transaction type"
            onChange={handleTransactionTypeChange}
          >
            {TRANSACTION_TYPE_OPTIONS.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="quantity"
          label={`Quantity (Item Unit: ${item?.unit || 'N/A'})`}
          rules={[{ required: true, message: 'Please enter quantity' }]}
        >
          <InputNumber
            placeholder="Enter quantity"
            min={0}
            step={1}
            style={{ width: '100%' }}
            onChange={handleQuantityChange}
          />
        </Form.Item>

        <Form.Item
          name="add_quantity"
          label={`${defaultTransactionType === 'PURCHASE' ? 'Received Quantity' : 'Usage Quantity'} (Auto-calculated)`}
          rules={[{ required: false }]}
        >
          <InputNumber
            placeholder="Auto-calculated based on item unit"
            min={0}
            step={1}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="remark" label="Remark" rules={[{ required: false }]}>
          <TextArea
            placeholder="Enter remark (optional)"
            rows={3}
            maxLength={255}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
